<template>
    <PublicPageLayout heading="§public.register_heading">
        <CoreStringInput
            v-if="asMaster"
            v-model="inputs.company_name"
            :validator="v$.company_name"
            :fullWidth="true"
            placeholder="§ui.placeholder_company_name"
            class="mb-10"
        />

        <CoreStringInput
            v-model="inputs.email"
            :validator="v$.email"
            :fullWidth="true"
            :disabled="true"
            placeholder="§ui.placeholder_login_email"
            class="mb-10"
        />

        <CorePasswordInput
            v-model="inputs.password"
            :validator="v$.password"
            :fullWidth="true"
            class="mb-10"
        />

        <CorePasswordInput
            v-model="inputs.passwordConfirm"
            :validator="v$.passwordConfirm"
            :fullWidth="true"
            class="mb-10"
            placeholder="§ui.placeholder_confirm_password"
        />

        <CoreDropdown
            v-model="inputs.preferred_locale"
            :options="getLocaleOptions"
            :validator="v$.preferred_locale"
            :fullWidth="true"
            placeholder="§ui.placeholder_select_language"
            class="mb-10"
        />

        <div class="mb-5 flex items-start">
            <CoreCheckbox v-model="inputs.accept" name="accept" />
            <label class="ml-2 text-left">
                {{
                    $t('§public.register-checkbox_text-part_1', {
                        company1_name: $t('§corporate_info.company1_name'),
                        company2_name: $t('§corporate_info.company2_name'),
                    })
                }}
                <span
                    @click="handleLinkClick('privacy_data_policy_link')"
                    class="text-primary cursor-pointer"
                >
                    {{ $t('§public.register-checkbox_text-privacy_data_policy') }}
                </span>
                {{ $t('§public.register-checkbox_text-part_2') }}
                <span @click="handleLinkClick('GTC_link')" class="text-primary cursor-pointer">
                    {{ $t('§public.register-checkbox_text-privacy_data') }}
                </span>
                {{ $t('§public.register-checkbox_text-part_3') }}
            </label>
        </div>

        <CoreButton
            @click="handleSubmit"
            label="§ui_button.save"
            icon="pi-save"
            :loading="waiting"
            :disabled="!isValid"
        />
    </PublicPageLayout>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required, requiredIf, minLength, maxLength, email as emailRule } from '@vuelidate/validators'
import PublicPageLayout from './components/PublicPageLayout.vue'
import CoreStringInput from '@/components/core/CoreStringInput.vue'
import CorePasswordInput from '@/components/core/CorePasswordInput.vue'
import CoreDropdown from '@/components/core/CoreDropdown.vue'
import CoreCheckbox from '@/components/core/CoreCheckbox.vue'
import CoreButton from '@/components/core/CoreButton.vue'
import { localeOptions } from '@/helpers/localeOptions'
import { useAuthStore } from '@/stores/auth'

const noSpaces = (value) => !/\s/.test(value)
const isSame = (inputs) => (value) => inputs.password === value
const mustAccept = (value) => value === true

export default {
    name: 'Register',

    components: {
        PublicPageLayout,
        CoreStringInput,
        CorePasswordInput,
        CoreDropdown,
        CoreCheckbox,
        CoreButton,
    },

    data() {
        return {
            inputs: {
                company_name: '',
                email: '',
                password: '',
                passwordConfirm: '',
                preferred_locale: '',
                accept: false,
            },
            asMaster: false,
            invite: '',
            waiting: false,
            v$: {},
            privacyLinks: {}, // Store privacy links from localStorage
        }
    },

    computed: {
        isValid() {
            return this.v$ ? !this.v$.$invalid : false
        },
        getLocaleOptions() {
            return localeOptions({ languagesOnly: true })
        },

        rules() {
            return {
                company_name: {
                    requiredIfMaster: requiredIf(this.asMaster),
                    minLength: minLength(2),
                    maxLength: maxLength(100),
                },
                email: { required, minLength: minLength(2), maxLength: maxLength(100), emailRule },
                password: {
                    required,
                    minLength: minLength(6),
                    maxLength: maxLength(100),
                    noSpaces,
                },
                passwordConfirm: {
                    required,
                    minLength: minLength(6),
                    maxLength: maxLength(100),
                    sameAsPassword: isSame(this.inputs),
                },
                preferred_locale: { required },
                accept: { mustAccept },
            }
        },
    },

    watch: {
        'inputs.preferred_locale'(newValue) {
            this.$i18n.locale = newValue
        },
    },

    created() {
        this.authStore = useAuthStore()
    },

    mounted() {
        try {
            const data = JSON.parse(localStorage.getItem('inviteData') || '{}')
            this.asMaster = data.as_master || false
            this.privacyLinks = data.privacy_links || {}
            this.v$ = useVuelidate(this.rules, this.inputs)
            this.setEmail(data.user_email || '')
            this.setInvite()
        } catch (error) {
            console.error('Failed to parse invite data from localStorage:', error)
            this.v$ = useVuelidate(this.rules, this.inputs)
            this.setInvite()
        }
    },

    unmounted() {
        localStorage.removeItem('inviteData')
    },

    methods: {
        async handleSubmit() {
            this.v$.$touch()
            if (this.waiting || this.v$.$invalid) return

            this.waiting = true
            try {
                const payload = {
                    invite: this.invite,
                    name: this.inputs.email,
                    password: this.inputs.password,
                    preferred_locale: this.inputs.preferred_locale,
                }
                if (this.asMaster)
                {
                    payload.company_name = this.inputs.company_name
                }
                await this.authStore.register(payload)
                this.waiting = false
            } catch (error) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.response?.data?.message || error.message || 'Registration failed',
                    life: 2000,
                })
                this.waiting = false
            }
        },

        setEmail(email) {
            this.inputs.email = email
        },

        setInvite() {
            const url = new URL(window.location.href)
            const parts = url.pathname.split('/')
            this.invite = parts[parts.length - 1]
        },

        handleLinkClick(documentName) {
            const locale = this.$i18n.locale.substring(0, 2)
            const linkKey = `${documentName}_${locale}`

            if (this.privacyLinks[linkKey]) {
                window.open(this.privacyLinks[linkKey], '_blank')
            } else {
                console.warn(`Privacy link not found for key: ${linkKey}`, this.privacyLinks)
            }
        },
    },
}
</script>
